import React from "react"

import { Form, Formik } from "formik"
import { NotificationManager } from "react-notifications"
import { gql, useMutation, useQuery } from "urql"

import { SelectOption } from "~tailwindui/types/SelectOption"
import { Color } from "~tailwindui/types/enums"

import { H2, H3, WithLoadingIndicator } from "~tailwindui/Basics"
import {
  Failures,
  FileInput,
  SelectInput,
  SubmitButton,
  TextInput,
  ToggleInput,
  handleFailure,
} from "~tailwindui/Form"
import CheckboxInput from "~tailwindui/Form/CheckboxInput"
import { buttonClassNames } from "~tailwindui/helpers/classNameHelpers"

import { timezoneOptions } from "src/enums"
import readFile from "src/readFile"
import { RunMutation } from "src/types"

import EducationHistory from "./EducationHistory"

const EditProfile: React.FC = () => {
  const [result, refetchQuery] = useQuery({ query })
  const [, runSaveMutation]: [any, RunMutation] =
    useMutation(editProfileMutation)

  const buildFormState = viewer => ({
    id: viewer.id,
    firstName: viewer.firstName || "",
    lastName: viewer.lastName || "",
    bio: viewer.bio || "",
    phoneNumber: viewer.phoneNumber || "",
    emailTimeZone: viewer.emailTimeZone,
    smsNotificationsEnabled: viewer.smsNotificationsEnabled,
    messageEmailsEnabled: viewer.messageEmailsEnabled,
    avatar: viewer.avatar || {},
    tierIds: viewer.tiers?.map(tier => tier.id) || [],
  })

  const handleSave = (values, actions) => {
    readFile(values.avatar)
      .then(file => {
        values.avatar = file
        runSaveMutation({ input: values })
          .then(response => {
            const { failures } = response.data.editTutorProfile
            if (failures.length > 0) {
              handleFailure(actions, failures)
            } else {
              NotificationManager.success("Your profile has been updated")
              refetchQuery({ requestPolicy: "network-only" })
            }
          })
          .catch(e => handleFailure(actions))
      })
      .catch(e => handleFailure(actions))
  }

  return (
    <div className="flex flex-col items-center">
      <H2>Profile</H2>
      <WithLoadingIndicator result={result}>
        {({ data: { viewer, tiers } }) => (
          <>
            <a
              href="/users/edit"
              className={buttonClassNames({
                color: Color.Orange,
              })}
            >
              Change Password
            </a>

            <div className="mb-10 sm:w-2/3 lg:w-1/2">
              <Formik
                initialValues={buildFormState(viewer)}
                onSubmit={handleSave}
              >
                <Form>
                  <H3 className="mt-12 border-b">Personal Information</H3>
                  <TextInput
                    name="firstName"
                    label="First Name"
                    autoFocus
                    autoComplete="given-name"
                  />
                  <TextInput
                    name="lastName"
                    label="Last Name"
                    autoComplete="family-name"
                  />
                  <TextInput name="phoneNumber" label="Phone Number" />
                  <SelectInput
                    label="Time Zone"
                    name="emailTimeZone"
                    options={timezoneOptions as SelectOption[]}
                  />
                  <FileInput
                    photo
                    name="avatar"
                    label="Avatar"
                    accept="image/*"
                    description="Use a square image to avoid cropping."
                  />
                  <ToggleInput
                    name="messageEmailsEnabled"
                    label="Receive Email Alerts for Messages"
                  />
                  <ToggleInput
                    name="smsNotificationsEnabled"
                    label="Disable Text Notifications"
                    description="This setting will not disable notifications for unbilled sessions."
                  />

                  <H3 className="mt-12 border-b">Tutoring Information</H3>
                  <CheckboxInput
                    checkboxType="multi"
                    name="tierIds"
                    label="Grade Ranges"
                    options={tiers.map(tier => ({
                      label: tier.gradeRange,
                      value: tier.id,
                    }))}
                  />
                  <EducationHistory />

                  <Failures />
                  <SubmitButton text="Save Profile" />
                </Form>
              </Formik>
            </div>
          </>
        )}
      </WithLoadingIndicator>
    </div>
  )
}

const query = gql`
  query TutorEditProfile {
    viewer {
      id
      firstName
      lastName
      bio
      phoneNumber
      emailTimeZone
      smsNotificationsEnabled
      messageEmailsEnabled
      avatar {
        filename
        url
      }
      tiers {
        id
      }
    }
    tiers {
      id
      gradeRange
    }
  }
`

const editProfileMutation = gql`
  mutation editTutorProfile($input: TutorInputObject!) {
    editTutorProfile(input: $input) {
      failures {
        message
      }
      tutor {
        id
      }
    }
  }
`

export default EditProfile
